import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { apiRequest } from "@/lib/queryClient";
import { STORY_BADGE_CATALOG, STORY_BADGE_IDS } from "../../../shared/profilePresets";

interface BadgePickerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedIds: string[];
  onSave: (ids: string[]) => void;
}

function BadgePickerDialog({ open, onOpenChange, selectedIds, onSave }: BadgePickerDialogProps) {
  const [chosen, setChosen] = useState<string[]>(selectedIds);

  const { data, isLoading } = useQuery<{ unlockedBadgeIds: string[] }>({
    queryKey: ["/api/user-profile/unlocked-badges"],
    enabled: open,
  });

  const unlockedIds = data?.unlockedBadgeIds ?? [];

  function toggle(id: string) {
    setChosen((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= 3) return prev;
      return [...prev, id];
    });
  }

  function handleOpen(isOpen: boolean) {
    if (isOpen) setChosen(selectedIds);
    onOpenChange(isOpen);
  }

  return (
    <Dialog open={open} onOpenChange={handleOpen}>
      <DialogContent className="max-w-sm" data-testid="badge-picker-dialog">
        <DialogHeader>
          <DialogTitle>Choose story badges</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-muted-foreground -mt-2">
          Select up to 3 badges · <span className="font-medium">{chosen.length}/3 selected</span>
        </p>
        {isLoading ? (
          <div className="space-y-2">
            {[1, 2, 3].map((i) => <Skeleton key={i} className="h-10 w-full" />)}
          </div>
        ) : (
          <div className="space-y-2 max-h-72 overflow-y-auto">
            {STORY_BADGE_IDS.map((id) => {
              const badge = STORY_BADGE_CATALOG[id];
              const isUnlocked = unlockedIds.includes(id);
              const isChecked = chosen.includes(id);
              const disabled = !isUnlocked || (!isChecked && chosen.length >= 3);
              return (
                <div
                  key={id}
                  className={`flex items-start gap-3 rounded-md p-2 transition-colors ${isUnlocked ? "hover:bg-muted" : "opacity-40"}`}
                >
                  <Checkbox
                    id={`badge-${id}`}
                    checked={isChecked}
                    disabled={disabled}
                    onCheckedChange={() => toggle(id)}
                    data-testid={`badge-checkbox-${id}`}
                  />
                  <Label htmlFor={`badge-${id}`} className={`flex-1 cursor-pointer ${disabled && !isChecked ? "cursor-not-allowed" : ""}`}>
                    <span className="font-medium">
                      {badge.emoji} {badge.label}
                    </span>
                    <p className="text-xs text-muted-foreground font-normal">
                      {isUnlocked ? badge.description : badge.unlockDescription}
                    </p>
                  </Label>
                </div>
              );
            })}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={() => { onSave(chosen); onOpenChange(false); }} data-testid="save-badges-btn">
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface StoryBadgesRowProps {
  selectedBadgeIds: string[];
}

export function StoryBadgesRow({ selectedBadgeIds }: StoryBadgesRowProps) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (ids: string[]) =>
      apiRequest("PATCH", "/api/user-profile", { storyBadgeIds: ids }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["personalised-profile"] });
    },
  });

  function handleSave(ids: string[]) {
    mutation.mutate(ids);
  }

  return (
    <div data-testid="story-badges-row">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs text-muted-foreground font-medium uppercase tracking-wide">
          Story badges
        </span>
        <button
          onClick={() => setPickerOpen(true)}
          className="text-muted-foreground hover:text-foreground transition-colors"
          data-testid="edit-badges-btn"
        >
          <Pencil className="h-3.5 w-3.5" />
        </button>
      </div>

      {selectedBadgeIds.length === 0 ? (
        <p className="text-xs text-muted-foreground italic">
          Badges will appear here as you complete key milestones
        </p>
      ) : (
        <div className="flex flex-wrap gap-2">
          {selectedBadgeIds.map((id) => {
            const badge = STORY_BADGE_CATALOG[id];
            if (!badge) return null;
            return (
              <span
                key={id}
                data-testid={`badge-pill-${id}`}
                className="inline-flex items-center gap-1.5 rounded-full bg-muted px-3 py-1 text-sm"
              >
                {badge.emoji} {badge.label}
              </span>
            );
          })}
        </div>
      )}

      <BadgePickerDialog
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        selectedIds={selectedBadgeIds}
        onSave={handleSave}
      />
    </div>
  );
}
